(ns common.transaction-process.templates.default
  "Generic transaction-process template for feature discovery and tenant testing.
   This intentionally stays customer-agnostic: it uses the shared TP engine and
   existing field model, but only the small field set from the 2026-06 default
   preparation/review mockups."
  (:require
   [common.schema.entities.attestation :as attestation-schema]
   [common.schema.entities.decision :as decision-schema]
   [common.schema.entities.decision-condition :as decision-condition-schema]
   [common.schema.entities.decision-participant :as decision-participant-schema]
   [common.schema.entities.declaration :as declaration-schema]
   [common.schema.entities.investment-reservation :as investment-reservation-schema]
   [common.schema.entities.transaction-process :as tp-schema]
   [common.transaction-process.templates.authoring :as a]))

(def template-id :transaction-process-template/default)

(def all-stages [:preparation :review :approval :signing :closing])
(def all-teams [:investment-team :platform-team :portfolio-manager])

(def team-roles
  [{:team-role/id    :investment-team
    :team-role/label "Investment team"}
   {:team-role/id    :platform-team
    :team-role/label "Platform team"}
   {:team-role/id    :portfolio-manager
    :team-role/label "Portfolio manager"}])

(def ^:private schema-lookup
  (into {} (concat tp-schema/schema
                   decision-schema/schema
                   decision-participant-schema/schema
                   decision-condition-schema/schema
                   attestation-schema/schema
                   declaration-schema/schema
                   investment-reservation-schema/schema)))

(def ^:private yes-no-options
  [{:value :yes :label "Yes"}
   {:value :no  :label "No"}])

(def ^:private investment-type-options
  [{:value :default-investment-type/equity      :label "Equity"}
   {:value :default-investment-type/debt        :label "Debt"}
   {:value :default-investment-type/real-estate :label "Real estate"}
   {:value :default-investment-type/fund        :label "Fund"}
   {:value :default-investment-type/other       :label "Other"}])

(def ^:private overview-fields
  [:transaction-process/short-name
   :transaction-process/deal-captain
   :transaction-process/investment-type
   :transaction-process/target-entities
   :transaction-process/base-case-irr
   :transaction-process/base-case-tmm])

(def ^:private transaction-detail-fields
  [:transaction-process/equity-amount
   :transaction-process/debt-amount
   :transaction-process/transaction-costs
   :transaction-process/taxes-amount
   :transaction-process/guarantee-amount
   :transaction-process/earnout-amount
   :transaction-process/investment-size-total])

(def ^:private coi-fields
  [:transaction-process/gp-affiliates-involved
   :transaction-process/lp-affiliates-involved
   :transaction-process/tax-avoidance-artificial-steps])

(def ^:private declaration-slot-layout
  [:transaction-process/legal-dd
   :transaction-process/financial-dd
   :transaction-process/tax-dd])

(def ^:private verification-slot-layout
  [:transaction-process/capacity-calculation
   :transaction-process/investment-restrictions
   :transaction-process/risk-mitigants-assessed
   :transaction-process/dd-conducted
   :transaction-process/tax-considerations])

(def ^:private decision-slot-layout
  [:transaction-process/pm-approval-decision
   :transaction-process/ic-decision])

(def ^:private decision-needed-fields
  [:transaction-process/pm-approval-decision-needed
   :transaction-process/ic-decision-needed])

(def ^:private document-slot-field-configs
  {:transaction-process-attachment/investment-memo
   (a/attachment-slot-field "Investment memo")

   :transaction-process-attachment/diligence-reports
   (a/attachment-slot-field "Diligence report(s)" {:field/multiple? true})

   :transaction-process-attachment/signing-documents
   (a/attachment-slot-field "Signing documents" {:field/multiple? true})

   :transaction-process-attachment/closing-documents
   (a/attachment-slot-field "Closing documents" {:field/multiple? true})})

(def ^:private prep-doc-slot-ids
  [:transaction-process-attachment/investment-memo
   :transaction-process-attachment/diligence-reports])

(def ^:private signing-doc-slot-ids
  [:transaction-process-attachment/signing-documents])

(def ^:private closing-doc-slot-ids
  [:transaction-process-attachment/closing-documents])

(def ^:private common-field-configs
  (merge
   {:transaction-process/short-name
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always}

    :transaction-process/deal-captain
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/investment-type
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/options       investment-type-options}

    :transaction-process/target-entities
    {:field/label         "Target"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/base-case-irr
    ;; IRR is a return metric that can legitimately exceed 100%, so exempt it from
    ;; the 0-100 cap applied to ownership-style "share of a whole" percentages.
    {:field/visibility           :visibility/always
     :field/uncapped-percentage? true
     :field/mandatoriness        :mandatory/optional}

    :transaction-process/base-case-tmm
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/equity-amount
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/debt-amount
    {:field/label         "Debt"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/transaction-costs
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/taxes-amount
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/guarantee-amount
    {:field/label         "Guarantees"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/earnout-amount
    {:field/label         "Earnouts"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/investment-size-total
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/gp-affiliates-involved
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/full-width    true
     :field/options       yes-no-options
     :field/default-value :no}

    :transaction-process/lp-affiliates-involved
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/full-width    true
     :field/options       yes-no-options
     :field/default-value :no}

    :transaction-process/tax-avoidance-artificial-steps
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always
     :field/full-width    true
     :field/options       yes-no-options
     :field/default-value :no}

    :transaction-process/pm-approval-decision-needed
    {:field/category      :decisions
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/default-value tp-schema/needed-value}

    :transaction-process/ic-decision-needed
    {:field/category      :decisions
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/default-value tp-schema/needed-value}

    :transaction-process/closing-amount
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/when-visible})

    :transaction-process/actual-closing-date
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/when-visible})

    :transaction-process/closing-amount-paid?
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/optional
            :field/data-type     :data.type/keyword
            :field/options       [{:value true  :label "Yes"}
                                  {:value false :label "No"}]})

    :transaction-process/closing-amount-paid-date
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/optional})

    :decision/date
    {:field/label         "Decision date"
     :field/category      :decisions
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always}

    :decision/notes
    {:field/category      :decisions
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision-participant/attendance
    {:field/category      :decisions
     :field/mandatoriness :mandatory/always}

    :decision-participant/role
    {:field/category      :decisions
     :field/mandatoriness :mandatory/always}

    :decision-participant/notes
    {:field/category      :decisions
     :field/mandatoriness :mandatory/optional}

    :decision-condition/title
    {:field/category      :decisions
     :field/mandatoriness :mandatory/always}

    :decision-condition/description
    {:field/category      :decisions
     :field/mandatoriness :mandatory/optional}

    :investment-reservation/amount
    {:field/category      :decisions
     :field/mandatoriness :mandatory/always}

    :investment-reservation/expiry-date
    {:field/category :decisions}

    :investment-reservation/void-date
    {:field/category :decisions}

    :investment-reservation/given-to
    {:field/category :decisions}

    :investment-reservation/given-by
    {:field/category :decisions}

    :investment-reservation/expiry-condition
    {:field/category :decisions}

    :investment-reservation/notes
    {:field/category :decisions}

    :attestation/outcome
    {:field/label         "Outcome"
     :field/category      :verifications
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always}

    :attestation/date
    {:field/label         "Date"
     :field/category      :verifications
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always}

    :attestation/comment
    {:field/category      :verifications
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :attestation/conditions
    {:field/category      :verifications
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :declaration/not-applicable?
    {:field/category      :declarations
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :declaration/completed?
    {:field/category      :declarations
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :declaration/date
    {:field/label         "Date"
     :field/category      :declarations
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/conditional
     :field/mandatoriness-rules [{:query-type :query-type/single-item
                                  :operator   :operand/any-of
                                  :field      :declaration/completed?
                                  :value      [true]}]}

    :declaration/comment
    {:field/category      :declarations
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}}
   document-slot-field-configs))

(def ^:private field-configs
  (a/enrich-field-configs common-field-configs schema-lookup))

(def ^:private sub-entity-namespaces
  #{"decision" "attestation" "declaration"
    "investment-reservation" "decision-participant" "decision-condition"})

(defn- top-level-entries
  [configs]
  (->> configs
       (remove (fn [[k _]]
                 (contains? sub-entity-namespaces (namespace k))))
       (map (fn [[k cfg]]
              [k (cond-> cfg
                   (= :data.type/attachment-slot (:field/data-type cfg))
                   (assoc :field/kind :field.kind/attachment-slot))]))
       (into {})))

(defn- leaves
  [configs field-kws]
  (into {} (map (fn [field-kw] [field-kw (get configs field-kw)]) field-kws)))

(defn- decision-participants-list
  [configs]
  {:field/kind       :field.kind/entity-list
   :slot/entity-type :entity.type/decision-participant
   :item-template    (leaves configs [:decision-participant/attendance
                                      :decision-participant/role
                                      :decision-participant/notes])})

(defn- decision-conditions-list
  [configs]
  {:field/kind       :field.kind/entity-list
   :slot/entity-type :entity.type/decision-condition
   :item-template    (leaves configs [:decision-condition/title
                                      :decision-condition/description])})

(def ^:private reservation-fields
  [:investment-reservation/amount
   :investment-reservation/expiry-date
   :investment-reservation/void-date
   :investment-reservation/given-to
   :investment-reservation/given-by
   :investment-reservation/expiry-condition
   :investment-reservation/notes])

(defn- decision-reservations-list
  [configs]
  {:field/kind       :field.kind/entity-list
   :slot/entity-type :entity.type/investment-reservation
   :item-template    (leaves configs reservation-fields)})

(defn- decision-slot
  [configs {:keys [label entity-type needed-field editable? required-by-default?
                   hide-when-not-needed? children]}]
  (let [children (set children)
        child-lists (cond-> {}
                      (children :participants)
                      (assoc :decision/participants (decision-participants-list configs))

                      (children :conditions)
                      (assoc :decision/conditions (decision-conditions-list configs))

                      (children :reservations)
                      (assoc :decision/investment-reservations
                             (decision-reservations-list configs)))]
    (cond-> {:field/kind       :field.kind/entity-slot
             :slot/entity-type entity-type
             :slot/label       label
             :slot/required    {:from-field  needed-field
                                :true-value  tp-schema/needed-value
                                :false-value tp-schema/not-needed-value
                                :default?    required-by-default?
                                :editable?   (boolean editable?)}
             :slot/template    (merge (leaves configs [:decision/date :decision/notes])
                                      child-lists)}
      hide-when-not-needed?
      (assoc :slot/hide-when-not-needed? true))))

(defn- attestation-slot
  [configs {:keys [label]}]
  {:field/kind       :field.kind/entity-slot
   :slot/entity-type :entity.type/attestation
   :slot/label       label
   :slot/required    {:default? true :editable? false}
   :slot/prefill     {:attestation/date :tp.prefill/today}
   :slot/template    {:attestation/outcome (assoc (get configs :attestation/outcome)
                                                  :field/options
                                                  attestation-schema/default-outcome-options)
                      :attestation/date    (get configs :attestation/date)
                      :attestation/comment (get configs :attestation/comment)}})

(defn- declaration-slot
  [configs {:keys [label]}]
  {:field/kind       :field.kind/entity-slot
   :slot/entity-type :entity.type/declaration
   :slot/label       label
   :slot/required    {:default? true :editable? false}
   :slot/template    (leaves configs [:declaration/not-applicable?
                                      :declaration/completed?
                                      :declaration/date
                                      :declaration/comment])})

(def ^:private decision-slot-specs
  [[:transaction-process/pm-approval-decision
    {:label        "PM approval"
     :entity-type  :entity.type/pm-approval-decision
     :needed-field :transaction-process/pm-approval-decision-needed
     :editable?    false
     :children     [:reservations :conditions]}]

   [:transaction-process/ic-decision
    {:label        "IC decision"
     :entity-type  :entity.type/ic-decision
     :needed-field :transaction-process/ic-decision-needed
     :required-by-default? true
     :hide-when-not-needed? true
     :editable?    true
     :children     [:participants :conditions]}]])

(def ^:private attestation-slot-specs
  [[:transaction-process/capacity-calculation
    {:label "Capacity calculation"}]
   [:transaction-process/investment-restrictions
    {:label "Investment restrictions"}]
   [:transaction-process/risk-mitigants-assessed
    {:label "Risk & mitigants assessed"}]
   [:transaction-process/dd-conducted
    {:label "DD conducted"}]
   [:transaction-process/tax-considerations
    {:label "Tax considerations"}]])

(def ^:private declaration-slot-specs
  [[:transaction-process/legal-dd
    {:label "Legal DD"}]
   [:transaction-process/financial-dd
    {:label "Financial DD"}]
   [:transaction-process/tax-dd
    {:label "Tax DD"}]])

(defn- build-dictionary
  [configs]
  (let [slot (fn [build specs]
               (into {} (map (fn [[k spec]] [k (build configs spec)]) specs)))]
    (merge (top-level-entries configs)
           (slot decision-slot decision-slot-specs)
           (slot attestation-slot attestation-slot-specs)
           (slot declaration-slot declaration-slot-specs))))

(defn- team-stage-permission
  [permission]
  (zipmap all-stages (repeat (zipmap all-teams (repeat permission)))))

(def ^:private write-all-permission (team-stage-permission :w))
(def ^:private read-all-permission (team-stage-permission :r))

(def ^:private editable-fields
  (vec (concat overview-fields
               (remove #{:transaction-process/investment-size-total}
                       transaction-detail-fields)
               coi-fields
               decision-needed-fields
               [:transaction-process/closing-amount
                :transaction-process/actual-closing-date
                :transaction-process/closing-amount-paid?
                :transaction-process/closing-amount-paid-date
                :transaction-process/team-assignments-edn
                :decision/date
                :decision/notes
                :decision-participant/attendance
                :decision-participant/role
                :decision-participant/notes
                :decision-condition/title
                :decision-condition/description
                :attestation/outcome
                :attestation/date
                :attestation/comment
                :attestation/draft?
                :attestation/conditions
                :attestation-condition/status
                :declaration/not-applicable?
                :declaration/completed?
                :declaration/date
                :declaration/comment]
               reservation-fields
               (keys document-slot-field-configs))))

(def ^:private permissions
  (merge (zipmap editable-fields (repeat write-all-permission))
         {:transaction-process/investment-size-total read-all-permission}))

(def summary-fields
  [{:summary/group  "Transaction"
    :summary/fields [:transaction-process/short-name
                     :transaction-process/vehicle
                     :transaction-process/status
                     :transaction-process/deal-captain
                     :transaction-process/investment-type
                     :transaction-process/target-entities]}
   {:summary/group  "Economics"
    :summary/fields [:transaction-process/equity-amount
                     :transaction-process/debt-amount
                     :transaction-process/transaction-costs
                     :transaction-process/taxes-amount
                     :transaction-process/guarantee-amount
                     :transaction-process/earnout-amount
                     :transaction-process/investment-size-total]}])

(def transitions
  [{:transition/from            :preparation
    :transition/to              :review
    :transition/forward-label   "Send for review"
    :transition/confirmation    "I confirm the transaction is ready to be reviewed"
    :transition/required-fields [:transaction-process/short-name]}
   {:transition/from           :review
    :transition/to             :approval
    :transition/forward-label  "Move forward"
    :transition/confirmation   "I confirm the transaction is ready to be approved"
    :transition/backward-label "Send back to previous stage"
    :transition/backward-target :preparation}
   {:transition/from           :approval
    :transition/to             :signing
    :transition/forward-label  "Move forward"
    :transition/confirmation   "I confirm the transaction is ready to be signed"
    :transition/backward-label "Send back to previous stage"
    :transition/backward-target :review}
   {:transition/from           :signing
    :transition/to             :closing
    :transition/forward-label  "Move forward"
    :transition/confirmation   "I confirm the transaction is ready to close"
    :transition/backward-label "Send back to previous stage"
    :transition/backward-target :approval}
   {:transition/from              :closing
    :transition/close-label       "Complete transaction"
    :transition/close-confirmation "I confirm this transaction is complete."
    :transition/close-action?     true
    :transition/required-fields   [:transaction-process/closing-amount
                                   :transaction-process/actual-closing-date]
    :transition/backward-label    "Send back to previous stage"
    :transition/backward-target   :signing}])

(def notifications
  [{:rule-id           :notify-creation
    :notification-type :creation
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: New transaction - {{process-name}}"
    :body              "A new transaction process has been created by {{created-by}}.\n\nName: {{process-name}}\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-prep-to-review
    :notification-type :stage-transition
    :from-stage        :preparation
    :to-stage          :review
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} moved to {{new-stage}}"
    :body              "{{process-name}} has been moved from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-backward-to-prep
    :notification-type :stage-transition
    :direction         :backward
    :to-stage          :preparation
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} sent back to {{new-stage}}"
    :body              "{{process-name}} has been sent back from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}])

(def stage-gates
  {:review   #{:declarations}
   :approval #{:attestations :declarations}
   :signing  #{:attestations :declarations :decisions}
   :closing  #{:attestations :declarations :decisions}})

(def decision-section-layout
  {:transaction-process/pm-approval-decision [:details :reservations :conditions :notes]
   :transaction-process/ic-decision          [:details :participants :conditions :notes]})

(def reservation-form-layout
  [[:investment-reservation/amount
    [:investment-reservation/expiry-date :investment-reservation/void-date]]
   [:investment-reservation/given-to :investment-reservation/given-by]
   [:investment-reservation/expiry-condition]
   [:investment-reservation/notes]])

(def stages
  [{:stage/id     :preparation
    :stage/label  "Preparation"
    :stage/layout :full-width
    :stage/cards  [{:card/type   :field-card
                    :card/id     :transaction-overview
                    :card/label  "Transaction overview"
                    :card/fields overview-fields}
                   {:card/type   :field-card
                    :card/id     :transaction-details
                    :card/label  "Transaction details"
                    :card/fields transaction-detail-fields}
                   {:card/type  :declarations
                    :card/id    :diligence-declarations
                    :card/label "Diligence declarations"
                    :card/slots declaration-slot-layout}
                   {:card/type   :field-card
                    :card/id     :conflicts-of-interest
                    :card/label  "Conflicts of interest"
                    :card/fields coi-fields}
                   {:card/type   :field-card
                    :card/id     :documents
                    :card/label  "Documents"
                    :card/fields prep-doc-slot-ids}
                   {:card/type  :transaction-teams
                    :card/id    :teams
                    :card/label "Transaction teams"}]}

   {:stage/id               :review
    :stage/label            "Reviewing"
    :stage/layout           :three-column
    :stage/decision-config? true
    :stage/cards            [{:card/type     :transaction-summary
                              :card/id       :summary
                              :card/position :left}
                             {:card/type     :decisions
                              :card/id       :decisions
                              :card/label    "Decisions needed"
                              :card/slots    decision-slot-layout
                              :card/position :center}
                             {:card/type     :verifications
                              :card/id       :verifications
                              :card/label    "Verifications"
                              :card/slots    verification-slot-layout
                              :card/position :center}]}

   {:stage/id     :approval
    :stage/label  "Approving"
    :stage/layout :three-column
    :stage/cards  [{:card/type     :transaction-summary
                    :card/id       :summary
                    :card/position :left}
                   {:card/type     :decisions
                    :card/id       :decisions
                    :card/label    "Decisions"
                    :card/slots    decision-slot-layout
                    :card/position :center}
                   {:card/type     :verifications
                    :card/id       :verifications
                    :card/label    "Verifications"
                    :card/slots    verification-slot-layout
                    :card/position :center}]}

   {:stage/id     :signing
    :stage/label  "Signing"
    :stage/layout :three-column
    :stage/cards  [{:card/type     :transaction-summary
                    :card/id       :summary
                    :card/position :left}
                   {:card/type     :field-card
                    :card/id       :signing-documents
                    :card/label    "Signing documents"
                    :card/fields   signing-doc-slot-ids
                    :card/position :center}]}

   {:stage/id     :closing
    :stage/label  "Closing"
    :stage/layout :three-column
    :stage/cards  [{:card/type     :transaction-summary
                    :card/id       :summary
                    :card/position :left}
                   {:card/type     :field-card
                    :card/id       :closing
                    :card/label    "Closing"
                    :card/fields   [:transaction-process/closing-amount
                                    :transaction-process/actual-closing-date
                                    :transaction-process/closing-amount-paid?
                                    :transaction-process/closing-amount-paid-date]
                    :card/position :center}
                   {:card/type     :field-card
                    :card/id       :closing-documents
                    :card/label    "Closing documents"
                    :card/fields   closing-doc-slot-ids
                    :card/position :center}]}])

(def template
  {:template/id          template-id
   :template/name        "Default transaction process"
   :template/summary-fields summary-fields
   :template/transaction-types []
   :template/team-roles  team-roles
   :template/stages      stages
   :template/transitions transitions
   :template/permissions permissions
   :template/fields      field-configs
   :template/dictionary  (build-dictionary field-configs)
   :template/stage-gates stage-gates
   :template/decision-sections decision-section-layout
   :template/reservation-layout reservation-form-layout
   :template/notifications notifications
   :template/stage-transition-notification-notice
   "You are about to move this transaction to the next stage."})
